/**
 * One Business Unit (f5.1) -- the Details tab, and only that tab.
 *
 * Two tabs the brief and the prototype imply are **absent, not disabled**,
 * following f3.1's precedent of omitting a control whose backing concept does
 * not exist rather than shipping one that explains itself away:
 *
 * * `Artifacts` -- artifacts arrive in phase 15;
 * * `Clients` -- clients arrived with f6.1 and have their own screen, reached
 *   from the count below. A tab here would duplicate it.
 *
 * The `Ownership` card beside the Details tab is **f4.2's component, reused**:
 * f5.1 parameterised it by node type rather than copying it, so this screen and
 * the Business Unit Group screen render the same card, the same assign dialog
 * and the same end-then-add replacement semantics. Its role selector offers
 * `CM` only, because the registry says that is what this level supports -- and
 * the label is the seeded one, **Client Manager**, never the prototype's
 * *"Commercial Manager"*.
 *
 * The four provenance values are columns on the unit's own row, stamped on
 * flush and read back with it. f1.5's audit viewer shows the actor as they were
 * named AT THE TIME; this screen shows who owns the row NOW, and the two are
 * allowed to differ after a rename.
 */

import { ArrowLeft, FileText, Info } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { useBusinessUnit, useLookupCatalog } from '@/api/queries';
import { OwnershipCard } from '@/ownership/OwnershipCard';
import { DELIVERY_ROLE_TYPE } from '@/routes/business-unit-groups/ownershipView';
import { ArtifactsPanel } from '@/routes/artifacts/ArtifactsPanel';
import { ASSIGN_OWNER, PUBLISH_ARTIFACT, rowCan } from '@/session/permissions';
import { RecordUnavailable } from '@/ui/RecordUnavailable';
import { TabHost } from '@/ui/TabHost';
import { GroupParentLink } from '@/ui/ScopedParentLink';

import {
  PROVENANCE_MISSING,
  UNIT_ROLE_CODES,
  badgeClassFor,
  currencyClassFor,
  formatActor,
  formatMoment,
  initialsOf,
} from './unitView';

function BackLink() {
  return (
    <Link className="btn btn-secondary btn-sm" to="/business-units">
      <ArrowLeft aria-hidden="true" /> Business Units
    </Link>
  );
}

/** One label-and-value pair in the Details tab. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="detail-meta-item">
      <div className="label">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function Provenance({
  label,
  at,
  by,
}: {
  label: string;
  at: string | null | undefined;
  by: { display: string } | null | undefined;
}) {
  const actor = formatActor(by);
  return (
    <Fact label={label}>
      <div>{formatMoment(at)}</div>
      <span className="row group-actor">
        {actor === PROVENANCE_MISSING ? (
          <span className="hint">{actor}</span>
        ) : (
          <>
            <span className="avatar sm" aria-hidden="true">
              {initialsOf(actor)}
            </span>
            {actor}
          </>
        )}
      </span>
    </Fact>
  );
}

export function BusinessUnitDetailPage() {
  const { unitId = '' } = useParams();
  // f9.2: the route guard in `App.tsx` reads the navigation key. The request
  // is always ISSUED, and the state is chosen from the response.
  const detail = useBusinessUnit(unitId, { enabled: Boolean(unitId) });
  const catalog = useLookupCatalog();
  const deliveryRoles =
    catalog.data?.types.find((type) => type.code === DELIVERY_ROLE_TYPE)?.values ?? [];

  if (detail.isPending) {
    return (
      <>
        <div className="page-head">
          <div>
            <h1 className="page-title">Business Unit</h1>
          </div>
          <div className="page-actions">
            <BackLink />
          </div>
        </div>
        <div className="card" aria-busy="true" aria-label="Loading the Business Unit">
          <div className="card-body">
            {[0, 1, 2, 3].map((index) => (
              <span key={index} className="skeleton-bar group-skeleton-line" />
            ))}
          </div>
        </div>
      </>
    );
  }

  if (detail.error || !detail.data) {
    // One shared state for all four levels (Q-f9.1-3).
    return (
      <RecordUnavailable
        error={detail.error}
        backTo="/business-units"
        backLabel="Business Units"
        onRetry={() => void detail.refetch()}
      />
    );
  }

  const unit = detail.data;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{unit.name}</h1>
          <div className="page-sub">
            <span className="mono">{unit.code}</span>, the unit of COM data entry
          </div>
        </div>
        <div className="page-actions">
          <BackLink />
        </div>
      </div>

      {/* f15.1: the shared TabHost -- Details unchanged, plus Artifacts. */}
      <TabHost
        label="Business Unit"
        tabs={[
          {
            id: 'details',
            label: 'Details',
            icon: <Info aria-hidden="true" />,
            panel: (
            <div className="detail-columns">
              <div className="card">
                <div className="card-body">
                  <div className="detail-meta">
                    <Fact label="Code">
                      <span className="mono">{unit.code}</span>
                    </Fact>
                    <Fact label="Name">{unit.name}</Fact>
                    <Fact label="Business Unit Group">
                      {/* A label, not a link, when the group is outside scope. */}
                      <GroupParentLink group={unit.business_unit_group} />
                    </Fact>
                    <Fact label="Reporting currency">
                      <span className={currencyClassFor(unit.reporting_currency.code)}>
                        {unit.reporting_currency.code}
                      </span>{' '}
                      {unit.reporting_currency.name}
                    </Fact>
                    <Fact label="Status">
                      <span className={badgeClassFor(unit.status.code)}>{unit.status.name}</span>
                    </Fact>
                    {/* f6.1's delivered count, on the Details tab this screen
                        already has -- no new tab, per f5.1's own deferral note. */}
                    <Fact label="Active clients">
                      {unit.client_count === 0 ? (
                        <span className="hint">None yet</span>
                      ) : (
                        <Link className="cell-link" to={`/clients?unit=${unit.id}`}>
                          {unit.client_count}
                        </Link>
                      )}
                    </Fact>
                    <Fact label="Kimble unit aliases">
                      {unit.aliases.length === 0 ? (
                        // An empty set is a real answer, not a gap: the DB unit's
                        // Kimble export carries no `Proposal: Unit` column at all, so
                        // an empty alias set there is correct rather than incomplete.
                        <span className="hint">None recorded</span>
                      ) : (
                        <span className="row alias-chips">
                          {unit.aliases.map((alias) => (
                            <span className="badge badge-chip" key={alias}>
                              {alias}
                            </span>
                          ))}
                        </span>
                      )}
                    </Fact>
                    <Provenance label="Created" at={unit.created_at} by={unit.created_by} />
                    <Provenance
                      label="Last changed"
                      at={unit.updated_at}
                      by={unit.updated_by}
                    />
                  </div>
                </div>
              </div>

              {/* f4.2's card, parameterised rather than copied. Its role selector
                  offers CM only, because the node-type registry says so. */}
              <OwnershipCard
                nodeType="BUSINESS_UNIT"
                nodeId={unit.id}
                nodeName={unit.name}
                roleCodes={UNIT_ROLE_CODES}
                deliveryRoles={deliveryRoles}
                mayManage={rowCan(unit, ASSIGN_OWNER)}
                emptyHint="No Client Manager is named on this unit yet. Any Atlas account can be assigned here, including one nobody has signed in to yet."
              />
            </div>
            ),
          },
          {
            id: 'artifacts',
            label: 'Artifacts',
            icon: <FileText aria-hidden="true" />,
            count: unit.artifact_count,
            panel: (
              <ArtifactsPanel
                ownerType="BUSINESS_UNIT"
                ownerId={unit.id}
                ownerName={unit.name}
                mayPublish={rowCan(unit, PUBLISH_ARTIFACT)}
              />
            ),
          },
        ]}
      />
    </>
  );
}
